<template>
  <div class="wrapper__content-sale-document col-container">
    <div class="col-header">
      <div class="col-header-left">
        <ButtonCirclePlusComponent
          v-if="$canOpen('sales.creditNote.add')"
          @onClick="creditNoteAdd"
        />
        <span class="col-title">Notas de crédito</span>
      </div>
      <div class="col-actions">
        <DownloadLote doc-type="credit-note" />
        <ButtonFilterComponent
          @onChangeFilters="onChangeFilters"
          @onApplyFilters="onApplyFilters"
          :setFilters="searchFilters"
          :loading="loading"
          :setStatus="filterStatus"
          placeholder="Buscar por número documento"
          isStatus
          isSearch
          isOrderBy
          isDates
        />
      </div>
    </div>
    <template>
      <WrapperScrollbarComponent @scrollingEnd="infiniteHandler">
        <CreditNoteCardComponent
          v-for="(creditNote, index) in filterCreditNotes"
          :key="creditNote.id || index"
          :creditNote="creditNote"
          @onDocUpdate="onDocUpdate"
          @onDocDeleted="onDocDeleted"
        />
        <infinite-loading
          @distance="90"
          @infinite="infiniteHandler"
          ref="infiniteLoading"
        >
          <div slot="spinner">
            <SkeletonOneCardComponent />
          </div>
          <div slot="no-more"></div>
          <div slot="no-results">
            <CreditNoteEmptyComponent v-if="!creditNotes.length" />
          </div>
        </infinite-loading>
      </WrapperScrollbarComponent>
    </template>
  </div>
</template>

<script>
import ButtonCirclePlusComponent from "../../../components/ButtonCirclePlus";
import ButtonFilterComponent from "../../../components/DrawerFilter";
import CreditNoteCardComponent from "./CreditNoteCard";
import DownloadLote from "@/modules/dashboard/sales/components/DownloadLote";
import SkeletonOneCardComponent from "@/components/Animations/Loading/SkeletonOneCard";
import WrapperScrollbarComponent from "../../../../components/WrapperScrollbar";
import CreditNoteEmptyComponent from "./Empty";

import creditNoteService from "../services/creditNoteService";

export default {
  name: "CreditNoteColumnsComponent",
  data() {
    return {
      loading: true,
      searchFilters: {},
      filterStatus: [
        {
          label: "Borrador",
          key: "draft"
        },
        {
          label: "Aprobada",
          key: "success"
        },
        {
          label: "Anulada",
          key: "nuled"
        },
        {
          label: "Fallida",
          key: "failed"
        }
      ],
      page: 1,
      creditNotes: [],
      breakScroll: false
    };
  },
  computed: {
    filterOrderBy() {
      const { orderBy } = this.searchFilters;
      return orderBy;
    },
    requestFilters() {
      const { search, from, to, status } = this.searchFilters;
      // Only include defined/non-null values to prevent API confusion
      const filters = {};
      if (search) filters.search = search;
      if (from) filters.from = from;
      if (to) filters.to = to;
      if (status) filters.status = status;
      return filters;
    },
    filterCreditNotes() {
      return this.creditNotes.concat().sort((a, b) => {
        if (this.filterOrderBy && this.filterOrderBy === "desc") {
          return a.total > b.total ? -1 : 1;
        } else if (this.filterOrderBy && this.filterOrderBy === "asc") {
          return a.total < b.total ? -1 : 1;
        }
        // Default: sort by date descending (most recent first)
        const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
        const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
        return dateB - dateA;
      });
    },
    getCreditNotesFilters() {
      const { documents } = this.$store.getters.getModule("sales");
      return documents.creditNotes.filters.get;
    }
  },
  methods: {
    goRouter(name, params = {}) {
      this.$router.push({ name, params });
    },
    creditNoteAdd() {
      this.goRouter("sales.creditNote.add");
    },
    updatedSearchFilters(filters) {
      this.searchFilters = {
        ...this.searchFilters,
        ...filters
      };

      this.$store.dispatch("salesDocumentsFiltersGet", {
        documentKey: "creditNotes",
        filters: this.searchFilters
      });
    },
    onChangeFilters(filters) {
      this.updatedSearchFilters(filters);
    },
    onApplyFilters() {
      this.onAsyncDataForDates();
    },
    onAsyncDataForDates() {
      this.creditNotes = [];
      this.page = 1;
      // El aviso de refresco llega con debounce: el componente puede haberse
      // desmontado (navegación) antes de que corra.
      if (!this.$refs.infiniteLoading) return;
      this.$refs.infiniteLoading.stateChanger.reset();
    },
    onDocUpdate({ id, data }) {
      const idx = this.creditNotes.findIndex(d => d.id === id);
      if (idx !== -1) this.$set(this.creditNotes, idx, data);
    },
    onDocDeleted(id) {
      this.creditNotes = this.creditNotes.filter(d => d.id !== id);
    },
    async getCreditNotes($state) {
      try {
        this.loading = true;
        let response = await creditNoteService
          .getCreditNotes({
            params: {
              ...{
                page: this.page
              },
              ...this.requestFilters
            }
          })
          .finally(() => {
            this.breakScroll = false;
            this.loading = false;
          });
        const { data } = response.data || {};
        // El interceptor de Api.js "silencia" los GET cancelados por deduplicación
        // devolviendo { data: {} }. Sin este guard, data.length revienta, el catch
        // se lo traga y $state nunca se resuelve: el infinite-loading queda colgado
        // y la columna solo vuelve a cargar recargando la página entera.
        if (!Array.isArray(data)) {
          $state.loaded();
          return;
        }

        if (data.length) {
          if (this.page === 1) {
            this.creditNotes = data;
          } else {
            // Deduplicate: only push items not already in the list
            const existingIds = new Set(this.creditNotes.map(c => c.id));
            const newItems = data.filter(item => !existingIds.has(item.id));
            if (newItems.length === 0) {
              // All returned items are already shown — no more unique data
              $state.complete();
              return;
            }
            newItems.forEach(item => this.creditNotes.push(item));
          }
          this.page = this.page + 1;
          $state.loaded();
        } else {
          $state.complete();
        }
      } catch (error) {
        return false;
      }
    },
    infiniteHandler($state) {
      if (!$state || typeof $state.complete !== "function") return;
      if (this.breakScroll) return false;
      this.breakScroll = true;
      this.getCreditNotes($state);
    }
  },
  components: {
    ButtonCirclePlusComponent,
    ButtonFilterComponent,
    CreditNoteCardComponent,
    SkeletonOneCardComponent,
    WrapperScrollbarComponent,
    CreditNoteEmptyComponent,
    DownloadLote
  },
  props: {},
  mounted() {
    this.searchFilters = this.getCreditNotesFilters;
  }
};
</script>

<style lang="scss" scoped></style>
